import { useEffect, useState, useCallback, useMemo, createRef } from "react";
import { useSelector } from "react-redux";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { TabPanel } from "@remo-co/ui-core/src/components/TabPanel";
import AppToken from "modules/appToken/AppToken";
import { selectMyCompany } from "modules/company/redux/selectors";
import { useI18n } from "i18n";
import { selectCompanySettings } from "modules/companySettings/redux/selectors";
import SsoConfiguration from "modules/auth/sso/SsoConfiguration";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import { selectUser } from "modules/auth/redux/selectors";
import CustomFloorPlansContainer from "modules/customFloorPlan/settings/CustomFloorPlansContainer";
import useAuth from "modules/auth/hooks/useAuth";
import { SettingsLeftMenu, IStep } from "../SettingsLeftMenu";
import CompanyProfile from "../../CompanyProfile";
import PlanBilling from "../../PlanBilling";
import { SettingsPages } from "../../constants/SettingsPages";
import { WhiteLabelSettings } from "../WhiteLabelSettings";
import { useStyles } from "./styles";

const Message = ({ message }: { message: string }) => (
  <Container fullWidth={false}>
    <Typography variant="h4" align="center">
      {message}
    </Typography>
  </Container>
);

interface SettingsContainerComponentProps {
  hasCompany: boolean;
  isCompanyOwner: boolean;
  steps: Record<string, IStep>;
  activeStep: string;
  setActiveStep: (step: string) => void;
  isManager: boolean;
}

export const SettingsContainerComponent = ({
  hasCompany,
  isCompanyOwner,
  steps,
  activeStep,
  setActiveStep,
  isManager,
}: SettingsContainerComponentProps): JSX.Element => {
  const classes = useStyles();
  const { t } = useI18n(["common", "profile", "sso"]);
  const tabPanelRef = createRef<HTMLDivElement>();
  const validateComponent = (component: JSX.Element) => (
    <>
      {hasCompany && isCompanyOwner && component}
      {hasCompany && !isCompanyOwner && (
        <Message message={t("profile:you.not.company.owner")} />
      )}
      {!hasCompany && <Message message={t("profile:you.not.have.company")} />}
    </>
  );

  return (
    <div className={classes.settingsContainer}>
      <SettingsLeftMenu
        steps={steps}
        activeStep={activeStep}
        onSetActiveStep={setActiveStep}
      />
      {activeStep === SettingsPages.companyProfile && (
        <TabPanel value={activeStep} index={SettingsPages.companyProfile}>
          <div className={classes.tabPanelWrapper}>
            {validateComponent(<CompanyProfile />)}
          </div>
        </TabPanel>
      )}
      {activeStep === SettingsPages.thirdPartyIntegration && (
        <TabPanel
          value={activeStep}
          index={SettingsPages.thirdPartyIntegration}
        >
          <div className={classes.tabPanelWrapper}>
            {validateComponent(<AppToken />)}
          </div>
        </TabPanel>
      )}

      {activeStep === SettingsPages.billing && (
        <TabPanel
          value={activeStep}
          index={SettingsPages.billing}
          className={classes.tabPanelWrapper}
        >
          <div>{validateComponent(<PlanBilling />)}</div>
        </TabPanel>
      )}

      {activeStep === SettingsPages.customFloorPlans && (
        <TabPanel value={activeStep} index={SettingsPages.customFloorPlans}>
          {hasCompany && (isCompanyOwner || isManager) && (
            <CustomFloorPlansContainer />
          )}
          {!hasCompany && (
            <Message message={t("profile:you.not.have.company")} />
          )}
        </TabPanel>
      )}

      {activeStep === SettingsPages.customBranding && (
        <TabPanel
          value={activeStep}
          index={SettingsPages.customBranding}
          ref={tabPanelRef}
        >
          {validateComponent(<WhiteLabelSettings />)}
        </TabPanel>
      )}

      {activeStep === SettingsPages.sso && (
        <TabPanel
          value={activeStep}
          index={SettingsPages.sso}
          ref={tabPanelRef}
        >
          {validateComponent(<SsoConfiguration tabPanelRef={tabPanelRef} />)}
        </TabPanel>
      )}
    </div>
  );
};

export const SettingsContainer = ({
  pageId,
}: {
  pageId: string;
}): JSX.Element => {
  const { t } = useI18n(["common", "profile"]);
  const company = useSelector(selectMyCompany);
  const companySettings = useSelector(selectCompanySettings);
  const { myCompanyPlan, isCustomSsoEnabled, areIntegrationsEnabled } =
    useCompanyPlanSettings();
  const user = useSelector(selectUser);

  const { isManager } = useAuth();
  const hasCompany = !!company;
  const isCompanyOwner =
    (user && company && company.owner === user.id) || false;

  const steps: Record<string, IStep> = useMemo(
    () => ({
      [SettingsPages.companyProfile]: {
        title: t("profile:company.profile"),
        visible: isCompanyOwner,
      },
      [SettingsPages.billing]: {
        title: t("profile:plan.billing"),
        visible: isCompanyOwner,
      },
      [SettingsPages.customFloorPlans]: {
        title: t("profile:custom.floor.plans"),
        visible: isCompanyOwner || isManager,
      },
      [SettingsPages.thirdPartyIntegration]: {
        title: t("profile:third.party.integrate"),
        visible: isCompanyOwner && areIntegrationsEnabled,
      },
      [SettingsPages.customBranding]: {
        title: t("settings:custom.branding"),
        visible: isCompanyOwner,
      },
      [SettingsPages.sso]: {
        title: t("sso:single.sign.on"),
        visible: isCustomSsoEnabled && isCompanyOwner,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isTrialCompleted(myCompanyPlan),
      isCompanyOwner,
      companySettings,
      t,
    ],
  );

  const getPageId = useCallback(() => {
    if (pageId && steps[pageId]) {
      return pageId;
    }

    const firstVisible = Object.entries(steps).find(
      (entry) => entry[1].visible,
    );

    return firstVisible ? firstVisible[0] : "";
  }, [pageId, steps]);

  const [activeStep, setActiveStep] = useState(getPageId());

  useEffect(() => {
    setActiveStep(getPageId());
  }, [getPageId]);

  return (
    <SettingsContainerComponent
      hasCompany={hasCompany}
      isCompanyOwner={isCompanyOwner}
      steps={steps}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      isManager={isManager}
    />
  );
};
